// ─────────────────────────────────────────────────────────────────────────────
// Workflow Engine API (ADR-0011).
//
// Every call goes through the shared axios client (§14b.1) — JWT and
// X-Division-Id are injected there, and it drives the global loading indicator
// automatically (§18.6). Never add a second axios instance.
// ─────────────────────────────────────────────────────────────────────────────
import { api } from '../../api/client';
import type {
  WorkflowDetail, WorkflowListRow, WorkflowModuleId, WorkflowPrepRow,
} from './types';

const BASE = '/api/v1/workflow-config';

/** The all-module catalog. `module` omitted = every module. */
export async function listWorkflows(module?: WorkflowModuleId): Promise<WorkflowListRow[]> {
  const res = await api.get(BASE, { params: module ? { module } : undefined });
  return res.data?.data ?? [];
}

/** Sample Preparation → Prep Methods: one row per configured prep step. */
export async function listPrepSteps(): Promise<WorkflowPrepRow[]> {
  const res = await api.get(`${BASE}/prep-steps`);
  return res.data?.data ?? [];
}

/** One workflow with its whole graph (the designer load). */
export async function getWorkflow(id: string): Promise<WorkflowDetail | null> {
  const res = await api.get(`${BASE}/${id}`);
  return res.data?.data ?? null;
}

export async function createWorkflow(input: unknown): Promise<string> {
  const res = await api.post(BASE, input);
  return res.data?.data?.id;
}

export async function updateWorkflow(id: string, input: unknown): Promise<void> {
  await api.put(`${BASE}/${id}`, input);
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

/**
 * Routing — which workflow applies to a scope key.
 *
 * `module` is REQUIRED: an Analysis workflow may legitimately share a Test with
 * a Preparation workflow, so resolving without it lets one shadow the other
 * (ADR-0011 §7).
 */
/**
 * Distinct values for a scope-key field, from the live Tests master data
 * (`dc2-tests` → `TestMethod`). Feeds the CheckedCombo pickers so Test / Matrix
 * / Method are a CLOSED list — a workflow can only ever be scoped to a test
 * that actually exists, which matters because that value is what routing
 * resolves on. A typo in a free-text box would produce a workflow that silently
 * never matches a sample.
 */
export async function listScopeOptions(column: 'test' | 'matrix' | 'method'): Promise<string[]> {
  const res = await api.get(`/api/v1/modules/dc2-tests/distinct/${column}`);
  // The modules distinct endpoint answers { success, values: [...] } — NOT the
  // usual { success, data }. Verified against the live API; reading `.data`
  // here silently yields an empty picker.
  const raw: unknown = res.data?.values ?? res.data?.data ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map(v => (typeof v === 'string' ? v : String((v as { value?: unknown })?.value ?? '')))
    .map(v => v.trim())
    .filter(v => v.length > 0 && !v.startsWith('F_'))   // drop the blank/non-blank sentinels
    .sort((a, b) => a.localeCompare(b));
}

export async function resolveWorkflow(
  module: WorkflowModuleId,
  scope: { test?: string; matrix?: string; method?: string },
): Promise<{ resolved: boolean; routable: boolean; reason: string | null }> {
  const res = await api.get(`${BASE}/resolve`, { params: { module, ...scope } });
  return res.data?.data;
}
